// src/components/ui/app-bar.tsx
import React from "react";
import { useAppBar } from "@/components/navigation/appbar-context";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button-new";
import { Menu, Search, Bell } from "lucide-react";
import trexLogo from "@/assets/trex-head-square-265.png";

import { useNavigationRail } from "@/components/navigation/rail-context";

interface AppBarProps {
    title?: React.ReactNode;
    actions?: React.ReactNode;
    showSearch?: boolean;
    showNotifications?: boolean;
    showUserMenu?: boolean;
    userAvatarUrl?: string;
    userName?: string;
    onSearchClick?: () => void;
    onNotificationsClick?: () => void;
    onUserMenuClick?: () => void;
    onSignOut?: () => void;
    className?: string;
    children?: React.ReactNode;
    showOnlyOnMobile?: boolean;
}

export function AppBar({
    title: propTitle,
    actions: propActions,
    showSearch = false,
    showNotifications = false,
    onSearchClick,
    onNotificationsClick,
    className,
    children,
    showOnlyOnMobile = false,
}: AppBarProps) {
    const { setDrawerOpen, isMobile } = useNavigationRail();

    const onMenuClick = () => {
        setDrawerOpen(true);
    };
    // Use context values if available, otherwise fall back to props
    const context = useAppBar();
    const title = context?.title || propTitle;
    const actions = context?.actions || propActions;
    const contextMenuClick = context?.onMenuClick;

    // Hide AppBar on non-mobile if showOnlyOnMobile is true
    if (showOnlyOnMobile && !isMobile) {
        return null;
    }

    return (
        <div
            className={cn(
                "sticky top-0 pt-2 pb-4 z-10 w-full",

                // "bg-surface/80 backdrop-blur-md backdrop-saturate-150 border-b border-outline-variant  supports-[backdrop-filter]:bg-surface/60",
                className,
            )}
        >
            <div
                className={cn(
                    "flex h-16 items-center pl-2 pr-4 ",
                    "bg-md-surface-level-3/75 shadow-lg rounded-full",
                    "container mx-auto",
                    "backdrop-blur-md",
                )}
            >
                {/* Left section */}
                <div className="flex items-center gap-3">
                    {/* App logo */}
                    <img
                        src={trexLogo}
                        alt="Receiptrex"
                        className="w-13 h-13 rounded-full object-cover"
                    />

                    {/* App title or custom content */}
                    {title && (
                        <div
                            className={cn(
                                "font-medium text-xl",
                                !isMobile && "font-semibold text-2xl",
                            )}
                        >
                            {title}
                        </div>
                    )}
                </div>

                {/* Center section - flexible space for breadcrumbs or other content */}
                <div className="flex-1 flex items-center justify-center md:justify-start ml-4">
                    {children}
                </div>

                {/* Right section - actions */}
                <div className="flex items-center gap-2">
                    {/* Custom actions */}
                    {actions}

                    {/* Mobile menu button - moved to right */}
                    {isMobile && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={contextMenuClick || onMenuClick}
                        >
                            <Menu className="h-6 w-6" />
                            <span className="sr-only">Menu</span>
                        </Button>
                    )}

                    {/* Search button */}
                    {showSearch && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onSearchClick}
                            className="text-on-surface-variant"
                        >
                            <Search className="h-5 w-5" />
                            <span className="sr-only">Search</span>
                        </Button>
                    )}

                    {/* Notifications */}
                    {showNotifications && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onNotificationsClick}
                            className="text-on-surface-variant"
                        >
                            <Bell className="h-5 w-5" />
                            <span className="sr-only">Notifications</span>
                        </Button>
                    )}

                    {/* User menu */}
                    {/* {showUserMenu && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="flex items-center gap-2 text-on-surface-variant ml-2"
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={userAvatarUrl} />
                                        <AvatarFallback>
                                            {userName ? (
                                                getInitials(userName)
                                            ) : (
                                                <User className="h-4 w-4" />
                                            )}
                                        </AvatarFallback>
                                    </Avatar>
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>
                                    My Account
                                </DropdownMenuLabel>
                                <DropdownMenuItem>
                                    <User className="mr-2 h-4 w-4" />
                                    <span>Profile</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                    <Settings className="mr-2 h-4 w-4" />
                                    <span>Settings</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>
                                    Administration
                                </DropdownMenuLabel>
                                <DropdownMenuItem asChild>
                                    <Link to="/users">
                                        <Users className="mr-2 h-4 w-4" />
                                        <span>Users</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/usage">
                                        <ChartColumnIncreasing className="mr-2 h-4 w-4" />
                                        <span>Usage</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Feedback</DropdownMenuLabel>
                                <DropdownMenuItem asChild>
                                    <SentryFeedbackButton className="w-full justify-start p-2 h-auto">
                                        <Bug className="mr-2 h-4 w-4" />
                                        <span className="text-sm">
                                            Send Bug Report
                                        </span>
                                    </SentryFeedbackButton>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={onSignOut}>
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Sign out</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )} */}
                </div>
            </div>
        </div>
    );
}
